import { tuneAbilityReach } from './AbilityReach';
/** AbilityBuilder facade: composition, modifiers and runtime assembly. */

import { BehaviorConfig } from './AbilityRegistry';
import type { ProjectileConfig, VFXConfig, AbilityFunction } from './AbilityBuilderTypes';
export { ABILITY_DEFAULTS } from './AbilityBuilderTypes';
export type { HomingConfig, ProjectileConfig, VFXConfig, BuffConfig, AbilityFunction } from './AbilityBuilderTypes';

import { TimelineScheduler } from './timeline/TimelineScheduler';

import type { HitEffect, ConditionFn, AbilityModifier, ElementType } from './modifiers/types';
import { applyPierce, applyBounce, applySplit, applyChain, applyAoeOnImpact, addOnHit, addOnCrit, addOnKill, applyElement, addConditional } from './DamageModifiers';
import { registerTrailVFX, registerImpactVFX, registerCastVFX, registerAuraVFX } from './AbilityPresets';
import { borrowProjectile, borrowBehaviors, borrowVFX, borrowVFXLayers } from './AbilityBorrowing';

import { createAbilityFunction, type BuildSnapshot } from './AbilityBuilderFactory';
import { PROJECTILE_VFX, shapeAspect } from '../../../../shared/config/projectile-vfx.config';

import { AbilityBuilderBehaviors } from './AbilityBuilderBehaviors';

class AbilityBuilder extends AbilityBuilderBehaviors {
  withProjectileFrom(sourceAbilityId: string, overrides?: Partial<ProjectileConfig>): this {
    const cfg = borrowProjectile(sourceAbilityId, overrides);
    return cfg ? this.withProjectile(cfg) : this;
  }

  /**
   * Clone behavior configs from another registered ability.
   * Optionally filter by behavior type.
   */
  withBehaviorFrom(sourceAbilityId: string, filterTypes?: BehaviorConfig['type'][]): this {
    const filtered = borrowBehaviors(sourceAbilityId, filterTypes);
    if (!filtered) return this;
    if (!this.metadata.behaviors) this.metadata.behaviors = [];
    this.metadata.behaviors.push(...filtered);
    return this;
  }

  /**
   * Clone VFX config from another registered ability.
   */
  withVFXFrom(sourceAbilityId: string, overrides?: Partial<VFXConfig>): this {
    const cfg = borrowVFX(sourceAbilityId, overrides);
    return cfg ? this.withVFX(cfg) : this;
  }

  /**
   * Cherry-pick specific VFX primitive layers from a registered VFX effect.
   * Layers are stored and merged into a composite effect at build time.
   */
  withVFXLayersFrom(effectId: string, layerIndices?: number[]): this {
    const layers = borrowVFXLayers(effectId, layerIndices);
    if (!layers) return this;
    if (!this._composedVFXLayers) this._composedVFXLayers = [];
    this._composedVFXLayers.push(...layers);
    return this;
  }

  // ---------------------------------------------------------------------------
  // Projectile Modifiers (delegates to DamageModifiers)
  // ---------------------------------------------------------------------------

  /** Projectile passes through N targets */
  pierce(count: number, damageReduction?: number): this {
    applyPierce(this.projectileModifiers, count, damageReduction);
    return this;
  }

  /** Projectile bounces to nearest target on hit */
  bounce(count: number, range: number, damageReduction?: number): this {
    applyBounce(this.projectileModifiers, count, range, damageReduction);
    return this;
  }

  /** Projectile splits into N children on hit */
  split(count: number, spreadAngle: number, damagePercent?: number): this {
    applySplit(this.projectileModifiers, count, spreadAngle, damagePercent);
    return this;
  }

  /** Projectile chains to nearby targets with reduced damage */
  chain(count: number, range: number, damageReduction?: number, vfxId?: string): this {
    applyChain(this.projectileModifiers, count, range, damageReduction, vfxId);
    return this;
  }

  /** Projectile explodes on impact for AoE damage */
  aoeOnImpact(radius: number, damagePercent: number, vfxId?: string): this {
    applyAoeOnImpact(this.projectileModifiers, radius, damagePercent, vfxId);
    return this;
  }

  // ---------------------------------------------------------------------------
  // On-Hit Effects (delegates to DamageModifiers)
  // ---------------------------------------------------------------------------

  /** Attach an effect that triggers on every hit */
  withOnHit(effect: HitEffect, id?: string): this {
    addOnHit(this.hitEffects, effect, id);
    return this;
  }

  /** Attach an effect that triggers only on critical hits */
  withOnCrit(effect: HitEffect, id?: string): this {
    addOnCrit(this.hitEffects, effect, id);
    return this;
  }

  /** Attach an effect that triggers when the hit kills the target */
  withOnKill(effect: HitEffect, id?: string): this {
    addOnKill(this.hitEffects, effect, id);
    return this;
  }

  // ---------------------------------------------------------------------------
  // Conditional Modifiers (delegates to DamageModifiers)
  // ---------------------------------------------------------------------------

  /** Apply a modifier only when a condition is met at cast time */
  when(condition: ConditionFn, modifier: AbilityModifier): this {
    addConditional(this.conditionals, condition, modifier);
    return this;
  }

  // ---------------------------------------------------------------------------
  // Element System (delegates to DamageModifiers)
  // ---------------------------------------------------------------------------

  /** Set the element type, auto-applying palette colors and default on-hit */
  withElement(element: ElementType): this {
    applyElement(element, this.metadata, this.projectileConfig, this.hitEffects);
    return this;
  }

  // ---------------------------------------------------------------------------
  // VFX Shorthands (delegates to AbilityPresets)
  // ---------------------------------------------------------------------------

  /** Auto-generate a trail VFX for this ability's projectile */
  withTrail(color: number, length?: number): this {
    const trailId = registerTrailVFX(this.metadata.id!, color, length);
    if (!this.metadata.vfx) this.metadata.vfx = {};
    this.metadata.vfx.trail = trailId;
    return this;
  }

  /**
   * Reference an ALREADY-registered trail-category def (TOML
   * `trail = "<def_id>"`, trails spec §5) instead of auto-generating one.
   * The def must contain a ribbon primitive to attach in flight.
   */
  withTrailDef(defId: string): this {
    if (!this.metadata.vfx) this.metadata.vfx = {};
    this.metadata.vfx.trail = defId;
    return this;
  }

  /** Apply a pre-built impact VFX effect */
  withImpactEffect(type: 'explosion' | 'spark' | 'shatter' | 'freeze' | 'burn'): this {
    const impactId = registerImpactVFX(this.metadata.id!, type);
    if (!this.metadata.vfx) this.metadata.vfx = {};
    this.metadata.vfx.impact = impactId;
    return this;
  }

  /** Apply a pre-built cast VFX effect */
  withCastEffect(type: 'charge' | 'glow' | 'summon' | 'channel'): this {
    const castId = registerCastVFX(this.metadata.id!, type);
    if (!this.metadata.vfx) this.metadata.vfx = {};
    this.metadata.vfx.cast = castId;
    return this;
  }

  /** Generate a persistent aura VFX */
  withAura(color: number, radius?: number, pulse?: boolean): this {
    const auraId = registerAuraVFX(this.metadata.id!, color, radius, pulse);
    if (!this.metadata.vfx) this.metadata.vfx = {};
    this.metadata.vfx.aura = auraId;
    return this;
  }

  /**
   * Retired screen shake / flash shorthand (plan 29 F8, lane BS 2026-10-11).
   * Nothing ever read what it stored; screen-wide feel now belongs to the
   * confirmed hit's tier alone (hitWeight → hitPresentation →
   * CombatFeedbackManager.triggerImpact), so a cast cannot shake or flash the
   * screen. Kept so the modules that call it still build; it records nothing.
   */
  withScreenEffect(_type: 'shake' | 'flash', _config?: { intensity?: number; duration?: number; color?: number }): this {
    return this;
  }

  // ---------------------------------------------------------------------------
  // Animation Timeline
  // ---------------------------------------------------------------------------

  /**
   * Enable timeline-based execution. Instead of running all behaviors
   * synchronously, events are scheduled at specific time offsets.
   * Use .atTime(ms) to schedule individual actions.
   */
  withTimeline(totalDurationMs: number): this {
    this._timelineDuration = totalDurationMs;
    this.metadata.castTime = totalDurationMs;
    return this;
  }

  /**
   * Schedule an action at a specific time offset (requires withTimeline first).
   * Returns a TimelineScheduler with .playVFX(), .spawnProjectile(), etc.
   */
  atTime(ms: number): TimelineScheduler {
    return new TimelineScheduler(this, ms);
  }

  // ---------------------------------------------------------------------------
  // Completion Callback
  // ---------------------------------------------------------------------------

  onComplete(callback: () => void): this {
    this.onCompleteCallback = callback;
    return this;
  }

  // ---------------------------------------------------------------------------
  // Snapshot - Capture builder state for factory assembly
  // ---------------------------------------------------------------------------

  snapshot(): BuildSnapshot {
    return {
      abilityId: this.metadata.id!,
      metadata: this.metadata,
      windUpDuration: this.windUpDuration,
      windUpVFX: this.windUpVFX,
      projectileConfig: this.projectileConfig,
      projectileVFX: this.projectileVFX,
      instantEffect: this.instantEffect,
      buffConfigs: [...this.buffConfigs],
      multiProjectiles: this.multiProjectiles,
      aoeConfig: this.aoeConfig,
      dashConfig: this.dashConfig,
      channelConfig: this.channelConfig,
      healConfig: this.healConfig,
      shieldConfig: this.shieldConfig,
      debuffConfig: this.debuffConfig,
      onCompleteCallback: this.onCompleteCallback,
      composedVFXLayers: this._composedVFXLayers,
      timelineDuration: this._timelineDuration,
      timelineEntries: this._timelineEntries,
      projectileModifiers: this.projectileModifiers,
      hitEffects: this.hitEffects,
      conditionals: this.conditionals,
      meleeConfig: this.meleeConfig,
      hitscanConfig: this.hitscanConfig,
      summonConfig: this.summonConfig,
      chainInto: this._chainInto,
      novaConfig: this.novaConfig,
      passiveConfig: this.passiveConfig,
      toggleConfig: this.toggleConfig,
      chargesConfig: this.chargesConfig,
      coneAOEConfig: this.coneAOEConfig,
      lineAOEConfig: this.lineAOEConfig,
      auraEffectConfig: this._auraEffectConfig,
      casterAuraConfig: this._casterAuraConfig,
    };
  }

  // ---------------------------------------------------------------------------
  // Build - Delegate runtime assembly to AbilityBuilderFactory
  // ---------------------------------------------------------------------------

  /**
   * Resolve projectile VISUAL size from the single canonical source.
   *
   * Priority: explicit per-ability override (`config.width/height` passed to
   * `withProjectile`) → the ability's TOML `vfx.projectileSize` (via
   * `withTomlData` → `metadata.tomlVfx`) → the shared `PROJECTILE_VFX` fallback.
   *
   * Before this, hand-authored abilities hardcoded width/height in their `.ts`
   * (e.g. mage_auto = 5) while `withTomlData` dropped the canonical
   * `projectileSize` (mage_auto = 8) on the floor — so the manual-builder path
   * and the TOML-factory path disagreed, and a basic attack rendered a different
   * size depending on which path registered it. Resolving here means EVERY path
   * that reads `metadata.projectile.width/height` (local `ProjectileBehavior`,
   * the remote `EntityEventWiring`) sees one data-sourced value. Runs at build()
   * so it's independent of `withProjectile`/`withTomlData` call order.
   *
   * `projectileSize` is a single scalar. Phase 0b makes it drive ASYMMETRIC
   * shapes too: the scalar is the travel-axis length (`width`) and the height is
   * derived from the shape's natural aspect (`SHAPE_ASPECT` in
   * `projectile-vfx.config`) — arrow scales 35:3, star 35:15, round stays 1:1.
   * So a single data value reproduces an arrow's thin profile without a
   * per-ability hardcoded width/height pair. An explicit per-ability override
   * (either dimension) ALWAYS wins over the derivation.
   */
  private resolveProjectileVisuals(): void {
    const p = this.metadata.projectile;
    if (!p) return;
    const tomlSize = (this.metadata as { tomlVfx?: { projectileSize?: number } }).tomlVfx?.projectileSize;
    const fallback = this.multiProjectiles ? PROJECTILE_VFX.multi : PROJECTILE_VFX.single;
    const aspect = shapeAspect(p.shape);
    if (p.width == null) p.width = tomlSize ?? fallback.width;
    if (p.height == null) {
      // Scalar source (TOML projectileSize): derive height from the resolved
      // width × shape aspect (arrow 35:3, star 35:15, round 1:1) so one data
      // value reproduces an asymmetric profile. No scalar → the shared fallback
      // already carries a complete width/height pair, use it verbatim.
      p.height = tomlSize != null ? Math.round(p.width * aspect) : fallback.height;
    }
  }

  build(): AbilityFunction {
    tuneAbilityReach(this.metadata, {
      projectile: this.projectileConfig, aoe: this.aoeConfig, melee: this.meleeConfig,
      cone: this.coneAOEConfig, line: this.lineAOEConfig, nova: this.novaConfig,
      aura: this._auraEffectConfig, summon: this.summonConfig,
    });
    this.resolveProjectileVisuals();
    return createAbilityFunction(this.snapshot());
  }
}

// =============================================================================
// FACTORY FUNCTION
// =============================================================================

export function createAbility(id: string): AbilityBuilder {
  return new AbilityBuilder(id);
}

export { AbilityBuilder };
